import { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useCms } from '@/hooks/useCms';
import type { CMSContextType } from '@/types/util.types';
import type { article } from '@/types/articles';
import { slugify } from '@/utils/slugify';

/**
 * Custom hook that returns the latest article based on current page type (author page, category page, or home page)
 * @returns The latest article for the current page type
 */
export function useLatestArticleByPage() {
  const params = useParams();
  const location = useLocation();
  const { latestArticle, categories, getArticlesByCategory, getArticlesByAuthor } = useCms() as CMSContextType;
  const [bannerArticle, setBannerArticle] = useState<article | null>(null);
  const previousCategorySlug = useRef<string | null>(null);

  const pageInfo = useMemo(() => {
    const isSubscribePage = location.pathname.includes('/subscribe');
    const isCategoryPage = location.pathname.includes('/category');
    const isAuthorPage = location.pathname.includes('/author');
    const isArticlePage = location.pathname.includes('/article');
    const isHomePage = location.pathname.includes('/home');
    const authorId = isAuthorPage ? Number(location.pathname.split('/author/')[1]) : null;
    const categorySlug = isCategoryPage ? params.category : null;

    return {
      isSubscribePage,
      isCategoryPage,
      isAuthorPage,
      isArticlePage,
      isHomePage,
      authorId,
      categorySlug,
    };
  }, [location.pathname, params.category]);

  useEffect(() => {
    async function fetchLatestArticle() {
      const categorySlugChanged = pageInfo.categorySlug !== previousCategorySlug.current;
      if (pageInfo.isCategoryPage && !categorySlugChanged) return;

      setBannerArticle(null);

      try {
        // If on an author page, get the latest article by that author.
        if (pageInfo.isAuthorPage && pageInfo.authorId) {
          const authorArticles = await getArticlesByAuthor(pageInfo.authorId);
          const hasAuthorArticles = authorArticles && authorArticles.articles.length > 0;

          if (hasAuthorArticles) {
            setBannerArticle(authorArticles.articles[0]);
            return;
          }
        }

        // If on a category page, get the latest article in that category.
        if (pageInfo.isCategoryPage && pageInfo.categorySlug) {
          const category = categories.find((category) => slugify(category.name) === pageInfo.categorySlug);

          if (!category) return;

          const categoryArticles = await getArticlesByCategory(category.id, 1);
          const hasCategoryArticles =
            categoryArticles && categoryArticles.articles && categoryArticles.articles.length > 0;

          if (hasCategoryArticles) {
            setBannerArticle(categoryArticles.articles[0]);
            previousCategorySlug.current = pageInfo.categorySlug;
            return;
          }
        }

        setBannerArticle(latestArticle || null);

        if (!pageInfo.isCategoryPage && !pageInfo.isAuthorPage) {
          previousCategorySlug.current = null;
        }
      } catch (error) {
        console.error('Error fetching latest article:', error);
        setBannerArticle(latestArticle || null);
      }
    }

    fetchLatestArticle();
  }, [pageInfo, latestArticle, getArticlesByAuthor, getArticlesByCategory, categories]);

  return useMemo(
    () => ({
      latestArticle: bannerArticle,
      isSubscribePage: pageInfo.isSubscribePage,
      isCategoryPage: pageInfo.isCategoryPage,
      isAuthorPage: pageInfo.isAuthorPage,
      isArticlePage: pageInfo.isArticlePage,
      isHomePage: pageInfo.isHomePage,
    }),
    [bannerArticle, pageInfo]
  );
}
